/**
 * Industry pages. These target "software development for <sector>" style
 * searches, which convert far better than generic service queries because
 * the searcher has already qualified themselves.
 *
 * Sectors chosen for the Calgary/Alberta economy. Each becomes a route, a
 * sitemap URL and a Service schema block scoped to that audience.
 */
module.exports = [
  {
    slug: 'startups-and-founders',
    name: 'Startups & Founders',
    icon: 'ux_design',
    tagline: 'Get the idea in front of real users before the runway runs out',
    metaTitle: 'Startup Software Development & MVP Builds | Codelithic Labs',
    metaDescription:
      'MVP and product development for founders worldwide. Scope tightly, ship early, and keep the code clean enough to survive investor due diligence.',
    intro:
      'Most startups don’t fail because the software was bad. They fail because they spent nine months building the wrong thing.',
    body: [
      'We help founders get to something real quickly: a clickable prototype you can put in front of users this month, then a working product that tests the assumption your whole business rests on. The job at this stage is learning, so the plan is built around what you need to find out rather than a feature list.',
      'Founders often come to us holding a quote for a full platform when what they need is a narrow test. We’ll say so. Cutting scope is usually the most valuable thing we do in the first conversation, and it costs you less.',
      'The code stays clean enough to hand over. When you raise, a technical reviewer will look at what you own, and when you hire your first engineers they need to be productive without calling us. We build for both from the start.',
    ],
    challenges: [
      'Every month spent building is a month not spent finding customers',
      'Requirements change weekly as you learn what users want',
      'No in-house engineering team yet, and hiring one takes months',
      'Quotes for a full platform when the idea still needs testing',
      'Code written fast that later fails investor technical review',
      'Founders locked in to whoever built version one',
    ],
    solutions: [
      { title: 'MVP scoping', desc: 'We cut the build down to what tests your riskiest assumption, and nothing else.' },
      { title: 'Clickable prototypes', desc: 'Something to show users and investors in weeks, before committing to a build.' },
      { title: 'Staged delivery', desc: 'A working product at the end of every iteration, so you can stop or change direction cheaply.' },
      { title: 'Fractional CTO support', desc: 'Senior technical judgement on architecture, hiring and vendors before you can hire it full time.' },
      { title: 'Due-diligence-ready code', desc: 'Tests, documentation and infrastructure that hold up when an investor’s reviewer looks.' },
      { title: 'Clean handover', desc: 'You own everything, so bringing engineering in-house later is a plan rather than a renegotiation.' },
    ],
    services: ['ui-ux-design', 'software-development', 'consulting'],
  },
  {
    slug: 'energy-and-resources',
    name: 'Energy & Resources',
    icon: 'cloud_solutions',
    tagline: 'Software for operators, service companies and the people who audit them',
    metaTitle: 'Software for Energy & Resources Companies | Codelithic Labs',
    metaDescription:
      'Custom software, data integration and security for energy and resources operators. Field data capture, reporting and compliance workflows.',
    intro:
      'Energy operations run on data that moves between field, office and regulator, usually through more manual steps than anyone would design on purpose.',
    body: [
      'We build the systems that sit between field data capture and the reports somebody has to file: ingestion from equipment and third-party feeds, validation that catches bad readings before they reach a submission, and audit trails that hold up when a regulator asks how a number was produced.',
      'Much of this work is integration rather than greenfield. You already own the systems; what’s missing is the layer that makes them agree with each other, and the interface that lets a field crew record something correctly on the first attempt.',
    ],
    challenges: [
      'Field data arriving as spreadsheets, PDFs and re-keyed paper',
      'Regulatory reporting assembled by hand under deadline',
      'Legacy systems that predate the integrations now required of them',
      'Remote sites with intermittent or no connectivity',
      'Security obligations that increase every year',
    ],
    solutions: [
      { title: 'Field data capture', desc: 'Offline-capable mobile tools that sync when a signal returns.' },
      { title: 'Reporting automation', desc: 'Regulatory submissions assembled from source data with a full audit trail.' },
      { title: 'Systems integration', desc: 'Connecting SCADA, ERP and reporting systems that were never designed to meet.' },
      { title: 'Security and access review', desc: 'Assessment against the obligations your operating agreements impose.' },
    ],
    services: ['software-development', 'cloud-solutions', 'cybersecurity'],
  },
  {
    slug: 'professional-services',
    name: 'Professional Services',
    icon: 'consulting',
    tagline: 'For firms whose product is their people’s time',
    metaTitle: 'Software for Professional Services Firms | Codelithic Labs',
    metaDescription:
      'Custom software and web development for law, accounting, engineering and consulting firms. Client portals, workflow and practice systems.',
    intro:
      'When you bill time, every hour lost to administration is inventory you can never sell again.',
    body: [
      'We build client portals, intake and matter workflows, and the integrations that stop the same client detail being typed into four systems. Most firms here don’t need another product. They already own five good ones that won’t talk to each other.',
      'Confidentiality shapes every decision. Access control, retention and audit logging are designed in from the start rather than added when a client questionnaire asks about them.',
    ],
    challenges: [
      'Client information re-entered across intake, billing and delivery systems',
      'Document-heavy workflows tracked in email',
      'Client questionnaires demanding security answers nobody can evidence',
      'Practice management software that fits most of the firm but not your specialism',
    ],
    solutions: [
      { title: 'Client portals', desc: 'Secure document exchange and status visibility that reduces status-chasing email.' },
      { title: 'Workflow automation', desc: 'Intake, conflict checks and matter progression with the handoffs made explicit.' },
      { title: 'Systems integration', desc: 'One client record shared across the tools you already pay for.' },
      { title: 'Security readiness', desc: 'Evidence for the client security reviews that now precede engagements.' },
    ],
    services: ['software-development', 'web-development', 'cybersecurity'],
  },
  {
    slug: 'logistics-and-transportation',
    name: 'Logistics & Transportation',
    icon: 'mobile_app',
    tagline: 'Visibility from dispatch to delivery',
    metaTitle: 'Software for Logistics & Transportation | Codelithic Labs',
    metaDescription:
      'Custom logistics software: dispatch, driver apps, proof of delivery, tracking and integrations with the carriers and systems you already use.',
    intro:
      'In logistics, the expensive problem is rarely moving the freight. It’s knowing where everything is without phoning someone.',
    body: [
      'We build dispatch tools, driver-facing mobile apps and customer tracking, plus the boring integration work that connects them to the carriers, telematics and accounting systems already in play.',
      'Driver apps get particular attention. They’re used with gloves on, in bad light, on poor connections, so they’re built offline-first and designed for the actual conditions rather than an office demo.',
    ],
    challenges: [
      'Dispatch coordinated by phone and whiteboard',
      'Proof of delivery on paper, scanned days later',
      'Customers calling for status because they have no other way to get it',
      'Telematics, TMS and accounting each holding a different version of the truth',
    ],
    solutions: [
      { title: 'Driver mobile apps', desc: 'Offline-first job lists, navigation handoff and electronic proof of delivery.' },
      { title: 'Dispatch tooling', desc: 'A live operational picture that replaces the whiteboard and the phone calls.' },
      { title: 'Customer tracking', desc: 'Self-serve status that removes a large share of inbound calls.' },
      { title: 'Carrier integration', desc: 'Rates, tracking and billing reconciled across partners automatically.' },
    ],
    services: ['mobile-app-development', 'software-development', 'cloud-solutions'],
  },
  {
    slug: 'healthcare-and-wellness',
    name: 'Healthcare & Wellness',
    icon: 'cybersecurity',
    tagline: 'Privacy-first systems for clinics and practitioners',
    metaTitle: 'Healthcare Software Development | Codelithic Labs',
    metaDescription:
      'Privacy-conscious healthcare software. Booking, intake and practice tools built around HIA, PIPEDA and the privacy rules that apply to you.',
    intro:
      'Privacy shapes healthcare software from the first sketch. Leave it to the end and you rebuild.',
    body: [
      'We build booking, intake and practice-support tools for clinics and independent practitioners, designed around Alberta’s Health Information Act and federal privacy obligations: data residency, access logging, retention and consent handled on purpose rather than assumed.',
      'We’re careful about scope here. We’re not a clinical systems vendor and we’ll say so: where a certified product exists for a regulated function, buying it’s the right call and we’ll help you integrate rather than replace it.',
    ],
    challenges: [
      'Patient information spread across booking, records and billing systems',
      'Intake still collected on paper and transcribed',
      'Privacy obligations that carry real consequences when breached',
      'Booking tools that don’t reflect how the practice schedules',
    ],
    solutions: [
      { title: 'Booking and scheduling', desc: 'Scheduling that matches your real availability rules and reduces no-shows.' },
      { title: 'Digital intake', desc: 'Consent-aware forms that land in your systems without transcription.' },
      { title: 'Privacy architecture', desc: 'Data residency, access logging and retention designed to your obligations.' },
      { title: 'Security assessment', desc: 'Review against HIA and PIPEDA requirements, with prioritised remediation.' },
    ],
    services: ['cybersecurity', 'software-development', 'ui-ux-design'],
  },
  {
    slug: 'construction-and-trades',
    name: 'Construction & Trades',
    icon: 'software_development',
    tagline: 'Tools that work on site, not just in the office',
    metaTitle: 'Software for Construction & Trades | Codelithic Labs',
    metaDescription:
      'Construction and trades software: job tracking, site reporting, quoting and scheduling built for use on site, offline and on mobile.',
    intro:
      'Software fails on construction sites for predictable reasons: it assumes a desk, a good connection and someone with time to fill in forms.',
    body: [
      'We build job tracking, daily site reporting, quoting and scheduling tools designed for a phone in a work glove: large targets, offline capture, and the absolute minimum number of taps to record what happened.',
      'The payoff is usually in the office. When site data arrives structured and same-day, invoicing gets faster, change orders stop getting lost, and job costing reflects reality rather than a guess assembled at month end.',
    ],
    challenges: [
      'Daily reports on paper, if they happen at all',
      'Change orders agreed verbally and disputed later',
      'Job costing that only becomes clear after the job is finished',
      'Scheduling across crews coordinated entirely by phone',
    ],
    solutions: [
      { title: 'Site reporting apps', desc: 'Fast offline capture of progress, hours, photos and incidents.' },
      { title: 'Quoting and estimating', desc: 'Consistent quotes from your own cost data, produced in minutes.' },
      { title: 'Crew scheduling', desc: 'A shared schedule that survives contact with weather and change orders.' },
      { title: 'Job costing integration', desc: 'Site data flowing into accounting without re-entry.' },
    ],
    services: ['mobile-app-development', 'software-development', 'web-development'],
  },
  {
    slug: 'retail-and-ecommerce',
    name: 'Retail & E-commerce',
    icon: 'digital_marketing',
    tagline: 'Storefronts that load fast and stock levels that are true',
    metaTitle: 'E-commerce & Retail Development | Codelithic Labs',
    metaDescription:
      'E-commerce and retail development: fast storefronts, inventory integration, and performance work that measurably improves conversion.',
    intro:
      'In retail, site speed shows up straight in your conversion rate. Shave a second off checkout and you can watch the numbers move.',
    body: [
      'We build and improve storefronts with performance treated as a feature: measured, budgeted and enforced on every deploy. For existing stores, our first engagement is usually an audit, because the fastest wins are often configuration and images rather than a replatform.',
      'The other half of the work is inventory truth. Selling something you don’t have is more expensive than not selling it, so integration between storefront, point of sale and supplier feeds tends to pay for itself quickly.',
    ],
    challenges: [
      'Storefronts slowed by accumulated apps and tracking scripts',
      'Online stock that disagrees with the shop floor',
      'Manual product data entry across multiple channels',
      'Checkout abandonment nobody has diagnosed',
    ],
    solutions: [
      { title: 'Performance optimisation', desc: 'Core Web Vitals work targeted at the pages that carry revenue.' },
      { title: 'Inventory integration', desc: 'One stock position shared across storefront, POS and suppliers.' },
      { title: 'Checkout analysis', desc: 'Instrumented funnels that show exactly where people leave.' },
      { title: 'Multi-channel listing', desc: 'Product data maintained once and published everywhere.' },
    ],
    services: ['web-development', 'digital-marketing', 'cloud-solutions'],
  },
];
